import {
  Car,
  CarPriceRange,
  FuelType,
  Language,
  Prop,
  Transmission,
} from "../types";

export const getPriceRangeText = (
  carPriceRange: CarPriceRange,
  language: Language,
) => {
  const dict = {
    [Language.Russian]: {
      [CarPriceRange.OneDay]: "1 день",
      [CarPriceRange.ThreeDays]: "2-3 дня",
      [CarPriceRange.EightDays]: "4-8 дней",
      [CarPriceRange.TwentyFiveDays]: "9-25 дней",
      [CarPriceRange.LongTerm]: "свыше 25 дней",
    },
    [Language.Spanish]: {
      [CarPriceRange.OneDay]: "1 día",
      [CarPriceRange.ThreeDays]: "2-3 días",
      [CarPriceRange.EightDays]: "4-8 días",
      [CarPriceRange.TwentyFiveDays]: "9-25 días",
      [CarPriceRange.LongTerm]: "más de 25 días",
    },
    [Language.Ukrainian]: {
      [CarPriceRange.OneDay]: "1 день",
      [CarPriceRange.ThreeDays]: "2-3 дня",
      [CarPriceRange.EightDays]: "4-8 днів",
      [CarPriceRange.TwentyFiveDays]: "9-25 днів",
      [CarPriceRange.LongTerm]: "більше 25 днів",
    },
    [Language.English]: {
      [CarPriceRange.OneDay]: "1 day",
      [CarPriceRange.ThreeDays]: "2-3 days",
      [CarPriceRange.EightDays]: "4-8 days",
      [CarPriceRange.TwentyFiveDays]: "9-25 days",
      [CarPriceRange.LongTerm]: "more than 25 days",
    },
  };
  return dict[language][carPriceRange];
};

export const getCarUrl = (car: Car): string => {
  return `${car.make} ${car.model}`.replaceAll(" ", "_") + `&id=${car.id}`;
};

export const carcDict = {
  [Language.English]: {
    pricesTitle: "Prices:",
    details: "Details",
    longTerm: "by agreement",
    euroPerDay: "€ per day",
    productionYear: "Year:",
    fuel: "Fuel:",
    fuelType: {
      [FuelType.Petrol]: "Petrol",
      [FuelType.Diesel]: "Diesel",
      [FuelType.Electric]: "Electric",
    },
    transmission: "Transmission:",
    transmissionType: {
      [Transmission.Manual]: "Manual",
      [Transmission.Automatic]: "Automatic",
    },
    seats: "Seats:",
    dailyKmIncluded: "Km per day included:",
    additionalKmPrice: "Price per additional km:",
    deposit: "Deposit:",
    props: "Features:",
    propsList: {
      [Prop.AirConditioning]: "Air Conditioning",
      [Prop.Bluetooth]: "Bluetooth",
      [Prop.Navigation]: "Navigation",
      [Prop.FrontParkingSensors]: "Front Parking Sensors",
      [Prop.RearParkingSensors]: "Rear Parking Sensors",
      [Prop.BackViewCamera]: "Back View Camera",
      [Prop.CruiseControl]: "Cruise Control",
      [Prop.HeatedSeats]: "Heated Seats",
      [Prop.LeatherInterior]: "Leather Interior",
      [Prop.StartStopButton]: "Start/Stop Button",
      [Prop.AndroidAuto]: "Android Auto",
      [Prop.AppleCarPlay]: "Apple CarPlay",
      [Prop.USBPort]: "USB Port",
      [Prop.KeylessEntry]: "Keyless Entry",
      [Prop.ForwardCollisionWarning]: "Forward Collision Warning",
      [Prop.Camera360]: "Camera 360",
      [Prop.WirelessCharging]: "Wireless Charging",
    },
  },

  [Language.Spanish]: {
    pricesTitle: "Precios:",
    details: "Detalles",
    longTerm: "por acuerdo",
    euroPerDay: "€ al día",
    productionYear: "Año:",
    fuel: "Combustible:",
    fuelType: {
      [FuelType.Petrol]: "Gasolina",
      [FuelType.Diesel]: "Diesel",
      [FuelType.Electric]: "Eléctrico",
    },
    transmission: "Transmisión:",
    transmissionType: {
      [Transmission.Manual]: "Manual",
      [Transmission.Automatic]: "Auto",
    },
    seats: "Asientos:",
    dailyKmIncluded: "Km diarios incluidos:",
    additionalKmPrice: "Precio por 1 km adicional:",
    deposit: "Fianza:",
    props: "Características:",
    propsList: {
      [Prop.AirConditioning]: "Aire Acondicionado",
      [Prop.Bluetooth]: "Bluetooth",
      [Prop.Navigation]: "Navegación",
      [Prop.FrontParkingSensors]: "Sensores de Estacionamiento Frontal",
      [Prop.RearParkingSensors]: "Sensores de Estacionamiento Trasero",
      [Prop.BackViewCamera]: "Cámara de Retrovisón",
      [Prop.CruiseControl]: "Control de Velocidad",
      [Prop.HeatedSeats]: "Asientos Calentados",
      [Prop.LeatherInterior]: "Interior de Cuero",
      [Prop.StartStopButton]: "Botón Encendido/Apagado",
      [Prop.AndroidAuto]: "Android Auto",
      [Prop.AppleCarPlay]: "Apple CarPlay",
      [Prop.USBPort]: "Puerto USB",
      [Prop.KeylessEntry]: "Entrada sin Llave",
      [Prop.ForwardCollisionWarning]: "Advertencia de Colisión Adelante",
      [Prop.Camera360]: "Cámara 360",
      [Prop.WirelessCharging]: "Carga Inalámbrica",
    },
  },

  [Language.Russian]: {
    pricesTitle: "Цены:",
    details: "Подробности",
    longTerm: "по договоренности",
    euroPerDay: "€ в день",
    productionYear: "Год:",
    fuel: "Топливо:",
    fuelType: {
      [FuelType.Petrol]: "Бензин",
      [FuelType.Diesel]: "Дизель",
      [FuelType.Electric]: "Электрический",
    },
    transmission: "Коробка:",
    transmissionType: {
      [Transmission.Manual]: "Механика",
      [Transmission.Automatic]: "Автомат",
    },
    seats: "Мест:",
    dailyKmIncluded: "Километров в день включено:",
    additionalKmPrice: "Цена за 1 км дополнительно:",
    deposit: "Залог:",
    props: "Опции:",
    propsList: {
      [Prop.AirConditioning]: "Кондиционер",
      [Prop.Bluetooth]: "Bluetooth",
      [Prop.Navigation]: "Навигация",
      [Prop.FrontParkingSensors]: "Передние датчики парковки",
      [Prop.RearParkingSensors]: "Задние датчики парковки",
      [Prop.BackViewCamera]: "Камера заднего вида",
      [Prop.CruiseControl]: "Поддержание скорости",
      [Prop.HeatedSeats]: "Обогрев сидений",
      [Prop.LeatherInterior]: "Кожаный салон",
      [Prop.StartStopButton]: "Кнопка Start/Stop",
      [Prop.AndroidAuto]: "Android Auto",
      [Prop.AppleCarPlay]: "Apple CarPlay",
      [Prop.USBPort]: "USB порт",
      [Prop.KeylessEntry]: "Без ключа",
      [Prop.ForwardCollisionWarning]: "Предупреждение о столкновении",
      [Prop.Camera360]: "Камера 360",
      [Prop.WirelessCharging]: "Беспроводная зарядка",
    },
  },
  [Language.Ukrainian]: {
    pricesTitle: "Ціни:",
    details: "Деталі",
    longTerm: "по домовленості",
    euroPerDay: "€ в день",
    productionYear: "Рік:",
    fuel: "Паливо:",
    fuelType: {
      [FuelType.Petrol]: "Бензин",
      [FuelType.Diesel]: "Дизель",
      [FuelType.Electric]: "Електричний",
    },
    transmission: "Коробка:",
    transmissionType: {
      [Transmission.Manual]: "Механика",
      [Transmission.Automatic]: "Автомат",
    },
    seats: "Місць:",
    dailyKmIncluded: "Кілометрів в день включено:",
    additionalKmPrice: "Ціна за 1 км додатково:",
    deposit: "Депозит:",
    props: "Опції:",
    propsList: {
      [Prop.AirConditioning]: "Кондиціонер",
      [Prop.Bluetooth]: "Bluetooth",
      [Prop.Navigation]: "Навігація",
      [Prop.FrontParkingSensors]: "Передні датчики парковки",
      [Prop.RearParkingSensors]: "Задні датчики парковки",
      [Prop.BackViewCamera]: "Камера заднего виду",
      [Prop.CruiseControl]: "Підтримання швидкості",
      [Prop.HeatedSeats]: "Обігрів сидінь",
      [Prop.LeatherInterior]: "Кожаний салон",
      [Prop.StartStopButton]: "Кнопка Start/Stop",
      [Prop.AndroidAuto]: "Android Auto",
      [Prop.AppleCarPlay]: "Apple CarPlay",
      [Prop.USBPort]: "USB порт",
      [Prop.KeylessEntry]: "Без ключів",
      [Prop.ForwardCollisionWarning]: "Попередження про зіткнення",
      [Prop.Camera360]: "Камера 360",
      [Prop.WirelessCharging]: "Безпроводная зарядка",
    },
  },
};

export const bookingDict = {
  [Language.English]: {
    title: "Order a car",
    bookingFormTitle: "Booking",
    fullName: "Full Name",
    fullNamePlaceholder: "Enter your full name",
    email: "Email",
    emailPlaceholder: "Enter your email",
    phone: "Phone",
    phonePlaceholder: "Enter your phone",
    message: "Message",
    messageOptional: "Message (optional)",
    messagePlaceholder: "Specify your wishes for the car",
    messageOptionalPlaceholder:
      "Specify your comments for the booking (optional)",
    pickUpPlace: "Car pick up location (City)",
    pickUpPlacePlaceholder: "Enter your pick up location",
    dropOffPlace: "Car drop off location (City)",
    dropOffPlacePlaceholder: "Enter your drop off location",
    pickUpDate: "Pick Up Date",
    dropOffDate: "Drop Off Date",
    pickUpTime: "Pick Up Time",
    dropOffTime: "Drop Off Time",
    submit: "Submit",
    repeat: "Repeat",
    erorr: {
      title: "Error happened while submitting the form!",
      text: "Our team will be notified and will fix this issue as soon as possible.",
    },
    success: {
      title: "Thank you for your submission!",
      text: "Our team will review it and get back to you as soon as possible.",
    },
    bookingSuccess: {
      title: "Thank you for your booking!",
      text: "Our team will contact you to discuss all the details as soon as possible.",
    },
    bookCar: "Book this car",
    olderThen24: "Driver is 25 years or older",
    drivingExperience: "Driver has 2 years or more of driving experience",
    carName: "Car",
    numberOfDays: "Number of days:",
    totalPrice: "Total rent price:",
    dailyPrice: "Daily price:",
    deposit: "Returnable deposit:",
    paymentAmount: "Amount to pay:",
    attentionText:
      "For delivery of the car more than 50 km from the city of Alicante, an additional charge of 1€ per 1 km is charged",
  },
  [Language.Spanish]: {
    bookingFormTitle: "Reserva",
    title: "Pedir un coche",
    fullName: "Nombre completo",
    fullNamePlaceholder: "Ingrese su nombre completo",
    email: "Correo electrónico",
    emailPlaceholder: "Ingrese su correo electrónico",
    phone: "Teléfono",
    phonePlaceholder: "Ingrese su teléfono",
    message: "Mensaje",
    messageOptional: "Mensaje (opcional)",
    messagePlaceholder: "Especifique sus deseos para el coche",
    messageOptionalPlaceholder:
      "Especifique sus comentarios para la reserva (opcional)",
    pickUpPlace: "Lugar de entrega (Ciudad)",
    pickUpPlacePlaceholder: "Ingrese su lugar de entrega",
    dropOffPlace: "Lugar de devolución (Ciudad)",
    dropOffPlacePlaceholder: "Ingrese su lugar de devolución",
    pickUpDate: "Fecha de entrega",
    dropOffDate: "Fecha de devolución",
    pickUpTime: "Hora de entrega",
    dropOffTime: "Hora de devolución",
    submit: "Enviar",
    repeat: "Repetir",
    erorr: {
      title: "Error ocurrido al enviar el formulario!",
      text: "Nuestro equipo será notificado y se resolverá este problema lo antes posible.",
    },
    success: {
      title: "Gracias por su mensaje!",
      text: "Nuestro equipo lo revisará y se pondrá en contacto con usted lo antes posible.",
    },
    bookingSuccess: {
      title: "Gracias por su reserva!",
      text: "Nuestro equipo se pondrá en contacto con usted para discutir todos los detalles lo antes posible.",
    },
    bookCar: "Reservar este coche",
    olderThen24: "El conductor tiene 25 años o más",
    drivingExperience: "El conductor tiene 2 años de experiencia o más",
    carName: "Coche",
    numberOfDays: "Número de días:",
    totalPrice: "Precio total del alquiler:",
    dailyPrice: "Precio diario:",
    deposit: "Depósito reembolsable:",
    paymentAmount: "Cantidad a pagar:",
    attentionText:
      "Para la entrega del coche más allá de 50 km de la ciudad de Alicante, se carga una tarifa adicional de 1€ por 1 km",
  },
  [Language.Russian]: {
    bookingFormTitle: "Бронирование",
    title: "Заказать автомобиль",
    fullName: "Имя и фамилия",
    fullNamePlaceholder: "Введите ваше имя и фамилию",
    email: "Электронная почта",
    emailPlaceholder: "Введите вашу электронную почту",
    phone: "Телефон",
    phonePlaceholder: "Введите ваш телефон",
    message: "Сообщение",
    messageOptional: "Сообщение (опционально)",
    messagePlaceholder: "Укажите ваши пожелания по автомобилю ",
    messageOptionalPlaceholder:
      "Укажите ваши комментарии по бронированию (опционально)",
    pickUpPlace: "Место получения авто (Город)",
    pickUpPlacePlaceholder: "Введите ваше место получения",
    dropOffPlace: "Место возврата авто (Город)",
    dropOffPlacePlaceholder: "Введите ваше место возврата",
    pickUpDate: "Дата получения",
    dropOffDate: "Дата возврата",
    pickUpTime: "Время получения",
    dropOffTime: "Время возврата",
    submit: "Отправить",
    repeat: "Повторить",
    erorr: {
      title: "Произошла ошибка при отправке формы!",
      text: "Наша команда будет уведомлена и исправит эту проблему как можно скорее.",
    },
    success: {
      title: "Спасибо за ваше сообщение!",
      text: "Наша команда изучит ваши пожелания и свяжется с вами как можно скорее.",
    },
    bookingSuccess: {
      title: "Спасибо за ваше бронирование!",
      text: "Наша команда свяжется с вами для обсуждения всех деталей как можно скорее.",
    },
    bookCar: "Забронировать этот авто",
    olderThen24: "Водителю 25 лет или более",
    drivingExperience: "Водитель имеет опыт вождения 2 года или более",
    carName: "Автомобиль",
    numberOfDays: "Количество дней:",
    totalPrice: "Общая стоимость аренды:",
    dailyPrice: "Цена в день:",
    deposit: "Возвращаемый залог:",
    paymentAmount: "Сумма к оплате:",
    attentionText:
      "При доставке авто дальше чем 50 км от города Аликанте, взимается дополнительная плата в размере 1€ за 1 км",
  },
  [Language.Ukrainian]: {
    bookingFormTitle: "Бронювання",
    title: "Замовити автомобіль",
    fullName: "Ім'я та прізвище",
    fullNamePlaceholder: "Введіть ваше ім'я та прізвище",
    email: "Електронна пошта",
    emailPlaceholder: "Введіть вашу електронну пошту",
    phone: "Телефон",
    phonePlaceholder: "Введіть ваш телефон",
    message: "Повідомлення",
    messageOptional: "Повідомлення (опціонально)",
    messagePlaceholder: "Вкажіть ваші побажання щодо автомобіля",
    messageOptionalPlaceholder:
      "Вкажіть ваші коментарі по бронюванню (опціонально)",
    pickUpPlace: "Місце отримання авто",
    pickUpPlacePlaceholder: "Введіть ваше місце отримання",
    dropOffPlace: "Місце повернення авто",
    dropOffPlacePlaceholder: "Введіть ваше місце повернення",
    pickUpDate: "Дата отримання",
    dropOffDate: "Дата повернення",
    pickUpTime: "Час отримання",
    dropOffTime: "Час повернення",
    submit: "Відправити",
    repeat: "Повторити",
    erorr: {
      title: "Виникла помилка при надсиланні форми!",
      text: "Наша команда буде повідомлена і вирішить цю проблему якнайшвидше.",
    },
    success: {
      title: "Дякуємо за ваше повідомлення!",
      text: "Наша команда вивчить ваші побажання і зв'яжеться з вами якнайшвидше.",
    },
    bookingSuccess: {
      title: "Дякуємо за ваше бронювання!",
      text: "Наша команда зв'яжеться з вами для обговорення всіх деталей якнайшвидше.",
    },
    bookCar: "Забронювати цей авто",
    olderThen24: "Водію 25 років або більше",
    drivingExperience: "Водій має досвід водіння 2 роки або більше",
    carName: "Автомобіль",
    numberOfDays: "Кількість днів:",
    totalPrice: "Загальна вартість аренди:",
    dailyPrice: "Ціна в день:",
    deposit: "Повертаємий депозит:",
    paymentAmount: "Сума до оплати:",
    attentionText:
      "При отриманні автомобіля більше ніж 50 км від міста Аліканте, стягується додаткова плата в розмірі 1€ за 1 км",
  },
};
